import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Activity, Store } from 'lucide-react';
import { accountingApi } from '@/services/accountingApi';
import { vendorApi } from '@/services/vendorApi';
import toast from 'react-hot-toast';

export const AccountingDashboard: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState<string>(
        new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
    );
    const [endDate, setEndDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [report, setReport] = useState<any>(null);
    const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
    const [selectedVendorId, setSelectedVendorId] = useState<string>('');

    useEffect(() => {
        loadVendors();
    }, []);

    useEffect(() => {
        loadReport();
    }, [startDate, endDate, selectedVendorId]);

    const loadVendors = async () => {
        try {
            const data = await vendorApi.getAllVendors({ status: 'active', limit: 200 });
            setVendors(
                (data.vendors || []).map((v: any) => ({
                    id: v.id,
                    name: `${v.firstName || ''} ${v.lastName || ''}`.trim() || v.email,
                }))
            );
        } catch {
            // Sessiz hata — vendor listesi opsiyonel
        }
    };

    const loadReport = async () => {
        setLoading(true);
        try {
            if (selectedVendorId) {
                // Vendor-specific rapor
                const data = await accountingApi.getVendorFinancialsSummary(selectedVendorId, {
                    dateStart: startDate,
                    dateEnd: endDate,
                });
                // VendorFinancialsSummary'yi global report formatına dönüştür
                setReport({
                    summary: {
                        totalRevenue: data.totalRevenue || 0,
                        totalCourierFees: data.totalExpenses || 0,
                        totalVendorCommissions: 0,
                        totalProfit: data.netProfit || 0,
                        totalOrders: data.orderCount || 0,
                        averageOrderValue: data.averageOrderValue || 0,
                        averageProfit: data.orderCount > 0 ? (data.netProfit || 0) / data.orderCount : 0,
                    },
                    vendorName: data.vendorName,
                    vendor: (data as any).vendor,
                });
            } else {
                const data = await accountingApi.getFinancialReport({
                    dateStart: startDate,
                    dateEnd: endDate,
                    type: 'global',
                });
                setReport(data);
            }
        } catch (error) {
            console.error('Rapor yüklenemedi:', error);
            toast.error('Finansal rapor yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p>Yükleniyor...</p>
            </div>
        );
    }

    const summary = report?.summary || {};

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                    Muhasebe Yönetimi
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Sistem finansal göstergelerini izleyin
                </p>
            </div>

            {/* Date Range Filter */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex flex-wrap gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Vendor
                        </label>
                        <div className="relative">
                            <Store className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <select
                                value={selectedVendorId}
                                onChange={(e) => setSelectedVendorId(e.target.value)}
                                className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                            >
                                <option value="">Tüm Vendor'lar</option>
                                {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        {v.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                {selectedVendorId && report?.vendorName && (
                    <div className="mt-3 text-sm text-blue-600 dark:text-blue-400">
                        <Store className="inline mr-1" size={14} />
                        Vendor: <strong>{report.vendorName}</strong>
                        {report.vendor?.commissionRate != null && (
                            <span className="ml-2 text-gray-500">
                                (Komisyon: %{report.vendor.commissionRate})
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Toplam Satış (Ciro)
                            </p>
                            <p className="text-3xl font-bold text-gray-800 dark:text-gray-100 mt-2">
                                ₺{(summary.totalRevenue || 0).toLocaleString('tr-TR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Brüt Platform Hacmi</p>
                        </div>
                        <DollarSign className="text-blue-600" size={32} />
                    </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                                Platform Komisyon Kazancı
                            </p>
                            <p className="text-3xl font-bold text-green-700 dark:text-green-300 mt-2">
                                ₺{(summary.platformCommission != null ? summary.platformCommission : (summary.totalVendorCommissions || 0)).toLocaleString(
                                    'tr-TR',
                                    {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }
                                )}
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">Bizim Brüt Gelirimiz</p>
                        </div>
                        <TrendingUp className="text-green-600" size={32} />
                    </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">
                                Satıcı Hakedişleri
                            </p>
                            <p className="text-3xl font-bold text-purple-700 dark:text-purple-300 mt-2">
                                ₺{(summary.vendorPayouts != null ? summary.vendorPayouts : ((summary.totalRevenue || 0) - (summary.totalVendorCommissions || 0))).toLocaleString(
                                    'tr-TR',
                                    {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }
                                )}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">Çiçekçilere Ödenecek</p>
                        </div>
                        <Activity className="text-purple-600" size={32} />
                    </div>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-red-600 dark:text-red-400">
                                Kurye Ödemeleri
                            </p>
                            <p className="text-3xl font-bold text-red-700 dark:text-red-300 mt-2">
                                ₺{(summary.totalCourierFees || 0).toLocaleString('tr-TR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </p>
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Kurye Hizmet Bedeli
                            </p>
                        </div>
                        <TrendingDown className="text-red-600" size={32} />
                    </div>
                </div>
            </div>

            {/* Stats Table */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                        İstatistikler
                    </h2>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">
                                Toplam Siparişler
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-100">
                                {summary.totalOrders || 0}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">
                                Ort. Sipariş Değeri
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-100">
                                ₺
                                {(summary.averageOrderValue || 0).toLocaleString('tr-TR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">
                                Ort. Kar/Sipariş
                            </span>
                            <span className="font-bold text-gray-800 dark:text-gray-100">
                                ₺
                                {(summary.averageProfit || 0).toLocaleString('tr-TR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                        Kar Analizi
                    </h2>
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Kar Marjı
                                </span>
                                <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                    {summary.totalRevenue > 0
                                        ? (
                                              ((summary.totalProfit || 0) /
                                                  summary.totalRevenue) *
                                              100
                                          ).toFixed(1)
                                        : '0'}
                                    %
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                    className="bg-green-600 h-2 rounded-full"
                                    style={{
                                        width: `${
                                            summary.totalRevenue > 0
                                                ? (
                                                      ((summary.totalProfit || 0) /
                                                          summary.totalRevenue) *
                                                      100
                                                  ).toFixed(1)
                                                : 0
                                        }%`,
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Kurye Maliyet Oranı
                                </span>
                                <span className="text-sm font-bold text-red-600 dark:text-red-400">
                                    {summary.totalRevenue > 0
                                        ? (
                                              ((summary.totalCourierFees || 0) /
                                                  summary.totalRevenue) *
                                              100
                                          ).toFixed(1)
                                        : '0'}
                                    %
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                    className="bg-red-600 h-2 rounded-full"
                                    style={{
                                        width: `${
                                            summary.totalRevenue > 0
                                                ? (
                                                      ((summary.totalCourierFees || 0) /
                                                          summary.totalRevenue) *
                                                      100
                                                  ).toFixed(1)
                                                : 0
                                        }%`,
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountingDashboard;
